// POC eyeshadow renderer for the photo-based VTO pipeline.
//
// Builds an "eyeshadow region" polygon from the upper-eyelid arc extended
// upward toward the brow bone, feathered so the edge fades softly, then
// fills with a vertical gradient that's strongest just above the lid
// crease and fades toward the brow.
//
// Uses `multiply` for matte/satin finishes, and an additional light
// `screen`-pass highlight near the inner lid for shimmer/metallic finishes.
// Same POC caveats as cheekOverlay: front-facing photo + uniform lighting
// required, no tilt correction.

import type { LipDetection } from '../tracking/faceLandmarker';
import type { SemanticFaceMap } from '../semantic/semanticFaceMap';
import { combineMasks, scaleMask, subtractMasks } from './maskUtils';
import { stackBlur } from './stackBlur';

export interface EyeOptions {
  hex: string;
  finish: string;  // matte/satin/shimmer/metallic/...
  opacity?: number;
  opacityScale?: number;
  shapeTightness?: number;
  featherScale?: number;
  sparkleScale?: number;
  semanticMap?: SemanticFaceMap | null;
}

export interface EyeRenderResult {
  canvas: HTMLCanvasElement;
}

// Upper-eyelid arc landmarks (outer→inner), one per eye. From the outer
// canthus along the upper-lid margin to the inner canthus — these bound
// the BOTTOM edge of the eyeshadow region.
const RIGHT_EYE_UPPER_LID = [33, 246, 161, 160, 159, 158, 157, 173, 133];
const LEFT_EYE_UPPER_LID  = [263, 466, 388, 387, 386, 385, 384, 398, 362];

// Outer / inner canthus indices per eye. First lid landmark = outer, last =
// inner; we reuse them to derive the eye's local "axis" for pose-aware
// lifting (Session A, v0.3.0).
const RIGHT_EYE_OUTER = 33;
const RIGHT_EYE_INNER = 133;
const LEFT_EYE_OUTER  = 263;
const LEFT_EYE_INNER  = 362;

// Brow bone landmarks (outer→inner), one per eye. Define the TOP edge of
// the eyeshadow region — the mask tapers toward these points.
const RIGHT_BROW = [70, 63, 105, 66, 107];
const LEFT_BROW  = [336, 296, 334, 293, 300];

interface EyeConfig {
  lidIndices: readonly number[];
  browIndices: readonly number[];
  outerIdx: number;
  innerIdx: number;
  lidMidIdx: number;
  browMidIdx: number;
}

const EYE_CONFIGS: readonly EyeConfig[] = [
  {
    lidIndices: RIGHT_EYE_UPPER_LID,
    browIndices: RIGHT_BROW,
    outerIdx: RIGHT_EYE_OUTER,
    innerIdx: RIGHT_EYE_INNER,
    lidMidIdx: 160,
    browMidIdx: 105
  },
  {
    lidIndices: LEFT_EYE_UPPER_LID,
    browIndices: LEFT_BROW,
    outerIdx: LEFT_EYE_OUTER,
    innerIdx: LEFT_EYE_INNER,
    lidMidIdx: 386,
    browMidIdx: 334
  }
];

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16)
  ];
}

function defaultOpacityFor(finish: string): number {
  // v0.3.0 (Session A rebalance wave): bumped ~25% from v0.1.2. The
  // previous translucent band was hidden next to the lip layer — users
  // in live co-design couldn't see the shadow without toggling the
  // Before/After view. New values stay below "sticker" territory (the
  // feather is generous enough that a higher alpha still reads as
  // diffuse pigment) but register at first glance.
  //
  // v0.4.1: shimmer/metallic bumped again because Session B freq-sep
  // flattened skin tone further, making nude shimmers like malizia's
  // #c49d88 (lum 0.64) practically invisible at 0.34 source-over.
  const f = finish.toLowerCase();
  if (f === 'shimmer')  return 0.31;
  if (f === 'metallic') return 0.35;
  if (f === 'sheer')    return 0.18;
  if (f === 'matte')    return 0.25;
  if (f === 'satin')    return 0.24;
  return 0.24;
}

// Rec. 709 relative luminance in 0..1.
function luminance([r, g, b]: [number, number, number]): number {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

// Luminance-aware alpha for shimmer/metallic base pass.
//
// v0.4.1 used a monotonic boost (+40% capped) that kept climbing past
// lum 0.80 and painted forever-young's #e8d4b8 (lum 0.84) as a white
// cream over the whole eye mask — and since the feather bleeds into the
// brow region, the brows looked covered.
//
// v0.4.3 uses a peaked curve: boost kicks in in the 0.55-0.72 band
// (skin-adjacent shades that otherwise disappear, malizia territory)
// and tapers back to 1.0 by lum 0.83. For shades brighter than 0.80
// a hard cap at 0.36 prevents the base pass from painting white cream
// regardless of the default alpha the finish would have asked for.
function sparkleBaseAlpha(defaultAlpha: number, lum: number): number {
  let boost = 1;
  if (lum > 0.55 && lum <= 0.72) {
    // Ramp up — peak at 0.72 reaches +35%.
    boost = 1 + (lum - 0.55) * 2.06;
  } else if (lum > 0.72 && lum <= 0.83) {
    // Ramp down — back to 1.0 at 0.83.
    boost = 1 + Math.max(0, 0.35 - (lum - 0.72) * 3.18);
  }
  let alpha = Math.min(0.56, defaultAlpha * boost);
  if (lum > 0.80) alpha = Math.min(alpha, 0.24);
  return alpha;
}

// Sheen screen-blend alpha also tapers with luminance. A bright shade
// already adds brightness via the base pass; stacking a full-strength
// screen of a lightened variant on top tips into white-out.
function sheenAlphaFor(finish: string, lum: number): number {
  const base = finish === 'metallic' ? 0.30 : 0.22;
  if (lum <= 0.75) return base;
  // Taper: 1× at lum 0.75, 0.5× at lum 0.875+
  const factor = Math.max(0.50, 1 - (lum - 0.75) * 4);
  return base * factor;
}

// Lightens an rgb triplet by `amount` (0..1) toward pure white; used to
// synthesise a "pearl highlight" variant of a shimmer/metallic shade.
function lighten(
  [r, g, b]: [number, number, number],
  amount: number
): [number, number, number] {
  return [
    Math.min(255, Math.round(r + (255 - r) * amount)),
    Math.min(255, Math.round(g + (255 - g) * amount)),
    Math.min(255, Math.round(b + (255 - b) * amount))
  ];
}

// Build a tinted-highlight layer for shimmer/metallic eyeshadows. The
// layer is the ORIGINAL photo's bright pixels inside the eye mask,
// remapped to a grayscale strength map, then multiplied with a
// lightened variant of the shade colour. When composited back with a
// `screen` blend, the result reads as pearl sparkle anchored to the
// actual light in the user's photo (brow-bone catch, inner-corner
// sheen) rather than a uniform wash of lightened colour.
function buildShimmerSheenLayer(
  source: HTMLCanvasElement | ImageBitmap,
  mask: HTMLCanvasElement,
  tint: [number, number, number],
  W: number,
  H: number,
  threshold: number
): HTMLCanvasElement | null {
  const sheen = document.createElement('canvas');
  sheen.width = W;
  sheen.height = H;
  const sctx = sheen.getContext('2d', { willReadFrequently: true });
  if (!sctx) return null;

  // 1. Original photo clipped to the eye mask.
  sctx.drawImage(source, 0, 0, W, H);
  sctx.globalCompositeOperation = 'destination-in';
  sctx.drawImage(mask, 0, 0);
  sctx.globalCompositeOperation = 'source-over';

  // 2. Convert bright pixels to a grayscale strength map (dim below
  //    threshold, bright above) so only the natural highlights survive.
  const img = sctx.getImageData(0, 0, W, H);
  const data = img.data;
  const range = 1 - threshold;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    if (lum < threshold) {
      data[i] = 0; data[i + 1] = 0; data[i + 2] = 0;
    } else {
      const k = Math.round(255 * ((lum - threshold) / range));
      data[i] = k; data[i + 1] = k; data[i + 2] = k;
    }
  }
  sctx.putImageData(img, 0, 0);

  // 3. Multiply the grayscale by the tint colour so bright pixels take
  //    on the lightened-shade hue, dark pixels stay black (a no-op under
  //    screen blend).
  sctx.globalCompositeOperation = 'multiply';
  sctx.fillStyle = `rgb(${tint[0]}, ${tint[1]}, ${tint[2]})`;
  sctx.fillRect(0, 0, W, H);

  // 4. Re-clip to the eye mask. v0.4.1 bug: Canvas 2D spec defines
  //    multiply compositing as `co = (1-ab)*cs + ab*B(cb, cs)`; when the
  //    destination alpha is 0 (outside the mask), that degenerates to
  //    `co = cs` with opaque alpha, i.e. the fillRect bleeds the tint
  //    colour across every transparent pixel. The downstream screen
  //    blend then brightens the whole image (not just the eye region).
  //    A second destination-in here erases the bled tint before the
  //    screen composite sees it.
  sctx.globalCompositeOperation = 'destination-in';
  sctx.drawImage(mask, 0, 0);
  sctx.globalCompositeOperation = 'source-over';

  return sheen;
}

// Chaikin-style smoothing: trace a closed polygon through the given points
// using quadratic Bézier curves. Each input point becomes a control point;
// the curve passes through the midpoints between consecutive inputs. This
// turns polylines into soft arcs that follow the natural curvature of the
// eye socket instead of looking like a geometric cut-out.
function smoothClosedPolygon(
  ctx: CanvasRenderingContext2D,
  points: readonly { x: number; y: number }[]
): void {
  if (points.length < 3) return;
  const n = points.length;
  const mid = (i: number, j: number) => ({
    x: (points[i]!.x + points[j]!.x) / 2,
    y: (points[i]!.y + points[j]!.y) / 2
  });
  // Start at the midpoint between the last and first point so the curve
  // is closed smoothly without a visible seam.
  const start = mid(n - 1, 0);
  ctx.moveTo(start.x, start.y);
  for (let i = 0; i < n; i++) {
    const cp = points[i]!;
    const end = mid(i, (i + 1) % n);
    ctx.quadraticCurveTo(cp.x, cp.y, end.x, end.y);
  }
  ctx.closePath();
}

// Derive the per-eye "face-up" unit vector from the canthus landmarks.
// Perpendicular to the outer→inner axis, flipped so it always points toward
// the brow (i.e. negative canvas-y for an upright face). Consistent across
// both eyes under any roll, and correct per-eye under yaw because the axis
// itself rotates with the landmarks. Falls back to straight-up in canvas
// coordinates if either corner landmark is missing.
function eyeUpUnit(
  detection: LipDetection,
  outerIdx: number,
  innerIdx: number
): { x: number; y: number } {
  const { landmarks, imageWidth: W, imageHeight: H } = detection;
  const outer = landmarks[outerIdx];
  const inner = landmarks[innerIdx];
  if (!outer || !inner) return { x: 0, y: -1 };
  const ax = (inner.x - outer.x) * W;
  const ay = (inner.y - outer.y) * H;
  const al = Math.hypot(ax, ay);
  if (al < 0.01) return { x: 0, y: -1 };
  // Perpendicular (ax, ay) → (-ay, ax). Normalise, then flip if it ended up
  // pointing down in canvas (positive y); we want it aimed at the brow.
  let ux = -ay / al;
  let uy = ax / al;
  if (uy > 0) { ux = -ux; uy = -uy; }
  return { x: ux, y: uy };
}

// Build the eyeshadow band for one eye as a single smooth closed polygon.
// Points go clockwise: lid arc (outer→inner) then brow arc (inner→outer).
// smoothClosedPolygon then draws quadratic curves through the midpoints,
// so the result follows the eye's natural almond shape.
//
// lidLift and browTighten control how aggressively the polygon is squeezed
// in from the raw landmark positions. v0.4.4: both default to 0.38 for
// matte/satin (diffuse powder look) but sparkle shades pass tighter values
// (0.32 / 0.55) so the feathered mask doesn't bleed onto the brow.
function traceEyePolygon(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  lidIndices: readonly number[],
  browIndices: readonly number[],
  outerIdx: number,
  innerIdx: number,
  lidLift: number,
  browTighten: number
): void {
  const { landmarks, imageWidth: W, imageHeight: H } = detection;
  const lid: { x: number; y: number }[] = [];
  const brow: { x: number; y: number }[] = [];

  for (const idx of lidIndices) {
    const p = landmarks[idx];
    if (!p) continue;
    lid.push({ x: p.x * W, y: p.y * H });
  }
  for (const idx of browIndices) {
    const p = landmarks[idx];
    if (!p) continue;
    brow.push({ x: p.x * W, y: p.y * H });
  }
  if (lid.length < 3 || brow.length < 2) return;

  // Face-frame "up" for this eye. Session A (v0.3.0): we derive the lift
  // direction from the canthus-to-canthus axis rather than using the raw
  // per-point nearest-brow vector. The lift MAGNITUDE is still adaptive
  // (outer lid lifts toward outer brow, inner toward inner) so the band
  // keeps its natural winged taper; only the direction is locked to the
  // eye's own up-axis so the mask is robust under head roll.
  const up = eyeUpUnit(detection, outerIdx, innerIdx);

  // Lift the lid edge upward (toward the brow). Magnitude scaled by the
  // per-finish lidLift factor. Smaller lift puts the band lower on the lid
  // (tight to the lash line) — used for sparkle finishes where the
  // feather is narrower so we want the band positioned more conservatively.
  const liftedLid = lid.map((l) => {
    let nearestBrow = brow[0]!;
    let minD = Infinity;
    for (const b of brow) {
      const dx = b.x - l.x;
      const dy = b.y - l.y;
      const d = dx * dx + dy * dy;
      if (d < minD) { minD = d; nearestBrow = b; }
    }
    // Project (nearestBrow - lid) onto the eye-up axis — this is the
    // component of the lift that moves the lid point toward the brow in
    // the face's own frame, with the sideways component discarded.
    const dx = nearestBrow.x - l.x;
    const dy = nearestBrow.y - l.y;
    const reach = dx * up.x + dy * up.y;
    const magnitude = reach * lidLift;
    return {
      x: l.x + up.x * magnitude,
      y: l.y + up.y * magnitude
    };
  });

  // Tighten the top boundary toward the nearest lid. For matte/satin the
  // default 0.38 keeps the band just below the brow bone; sparkle shades
  // pass 0.55 to pull the polygon edge well under the brow hair, because
  // a bright shimmer's feathered spillover would otherwise paint over
  // the brow (forever-young #e8d4b8 was the triggering case at v0.4.3).
  const tightenedBrow = brow.map((b) => {
    let nearestLid = liftedLid[0]!;
    let minD = Infinity;
    for (const l of liftedLid) {
      const dx = l.x - b.x;
      const dy = l.y - b.y;
      const d = dx * dx + dy * dy;
      if (d < minD) { minD = d; nearestLid = l; }
    }
    const dx = nearestLid.x - b.x;
    const dy = nearestLid.y - b.y;
    // Project onto -up (brow moves down-on-face toward lid), so reach is
    // the magnitude along the face-down direction at this eye.
    const reach = -(dx * up.x + dy * up.y);
    const magnitude = reach * browTighten;
    return {
      x: b.x - up.x * magnitude,
      y: b.y - up.y * magnitude
    };
  });

  // Combine into a single clockwise loop: lid arc (outer→inner), then
  // brow arc reversed (inner→outer). smoothClosedPolygon turns the
  // polyline into a smooth closed curve via quadratic Bézier segments.
  const loop: { x: number; y: number }[] = [];
  for (const p of liftedLid) loop.push(p);
  for (let i = tightenedBrow.length - 1; i >= 0; i--) loop.push(tightenedBrow[i]!);

  smoothClosedPolygon(ctx, loop);
}

// Shape parameters per finish. Sparkle shades (shimmer/metallic) get a
// tighter polygon (less lift, more brow tightening) and a smaller feather
// because their bright pigment makes spillover onto the brow visible in
// a way matte/satin shadows don't exhibit.
interface EyeShapeParams {
  lidLift: number;
  browTighten: number;
  featherRatio: number;  // fraction of short-edge in px
}

const SHAPE_DEFAULT: EyeShapeParams = {
  lidLift: 0.30,
  browTighten: 0.42,
  featherRatio: 0.016
};

const SHAPE_SPARKLE: EyeShapeParams = {
  lidLift: 0.26,
  browTighten: 0.50,
  featherRatio: 0.014
};

function buildSingleEyeMask(
  detection: LipDetection,
  config: EyeConfig,
  params: EyeShapeParams = SHAPE_DEFAULT
): HTMLCanvasElement {
  const W = detection.imageWidth;
  const H = detection.imageHeight;
  const mask = document.createElement('canvas');
  mask.width = W;
  mask.height = H;
  const mctx = mask.getContext('2d', { willReadFrequently: true });
  if (!mctx) return mask;

  // Feather scales with image size. Default 1.4% short-edge produces the
  // "diffuse powder" look (v0.1.2). Sparkle uses 1.0% — tighter mask so
  // the bright pigment doesn't bleed onto the brow hair.
  const feather = Math.max(3.0, Math.min(W, H) * params.featherRatio);

  mctx.beginPath();
  traceEyePolygon(
    mctx,
    detection,
    config.lidIndices,
    config.browIndices,
    config.outerIdx,
    config.innerIdx,
    params.lidLift,
    params.browTighten
  );
  mctx.fillStyle = '#ffffff';
  mctx.fill();

  stackBlur(mctx, 0, 0, W, H, feather);

  return mask;
}

function buildEyeMask(
  detection: LipDetection,
  params: EyeShapeParams = SHAPE_DEFAULT
): HTMLCanvasElement {
  const W = detection.imageWidth;
  const H = detection.imageHeight;
  const mask = document.createElement('canvas');
  mask.width = W;
  mask.height = H;
  const mctx = mask.getContext('2d', { willReadFrequently: true });
  if (!mctx) return mask;

  for (const config of EYE_CONFIGS) {
    mctx.drawImage(buildSingleEyeMask(detection, config, params), 0, 0);
  }

  return mask;
}

function safeSemanticEyeMasks(
  semanticMap: SemanticFaceMap,
  W: number,
  H: number
): [HTMLCanvasElement, HTMLCanvasElement] | null {
  return [
    subtractMasks(scaleMask(semanticMap.masks.rightMobileLid, W, H), [
      scaleMask(semanticMap.masks.rightEye, W, H),
      scaleMask(semanticMap.masks.rightBrow, W, H)
    ]),
    subtractMasks(scaleMask(semanticMap.masks.leftMobileLid, W, H), [
      scaleMask(semanticMap.masks.leftEye, W, H),
      scaleMask(semanticMap.masks.leftBrow, W, H)
    ])
  ];
}

function safeSemanticCreaseMasks(
  semanticMap: SemanticFaceMap,
  W: number,
  H: number
): [HTMLCanvasElement, HTMLCanvasElement] | null {
  return [
    subtractMasks(scaleMask(semanticMap.masks.rightCreaseFade, W, H), [
      scaleMask(semanticMap.masks.rightEye, W, H),
      scaleMask(semanticMap.masks.rightBrow, W, H)
    ]),
    subtractMasks(scaleMask(semanticMap.masks.leftCreaseFade, W, H), [
      scaleMask(semanticMap.masks.leftEye, W, H),
      scaleMask(semanticMap.masks.leftBrow, W, H)
    ])
  ];
}

function semanticLashLineMask(
  semanticMap: SemanticFaceMap | null | undefined,
  W: number,
  H: number
): HTMLCanvasElement | null {
  if (!semanticMap) return null;
  return combineMasks(W, H, [
    semanticMap.masks.rightLashLine,
    semanticMap.masks.leftLashLine
  ]);
}

function tunedShapeParams(
  base: EyeShapeParams,
  shapeTightness = 0,
  featherScale = 1
): EyeShapeParams {
  const t = Math.max(0, Math.min(1, shapeTightness));
  return {
    lidLift: base.lidLift * (1 - 0.22 * t),
    browTighten: Math.min(0.68, base.browTighten + 0.12 * t),
    featherRatio: Math.max(0.011, base.featherRatio * featherScale)
  };
}

export function renderEyeOverlay(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection,
  options: EyeOptions
): EyeRenderResult {
  const W = detection.imageWidth;
  const H = detection.imageHeight;
  const out = document.createElement('canvas');
  out.width = W;
  out.height = H;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');

  ctx.drawImage(source, 0, 0, W, H);

  const finish = options.finish.toLowerCase();
  const isSparkle = finish === 'shimmer' || finish === 'metallic';
  const shapeParams = tunedShapeParams(
    isSparkle ? SHAPE_SPARKLE : SHAPE_DEFAULT,
    options.shapeTightness ?? 0,
    options.featherScale ?? 1
  );
  const semanticEyeMasks = options.semanticMap
    ? safeSemanticEyeMasks(options.semanticMap, W, H)
    : null;
  const semanticCreaseMasks = options.semanticMap
    ? safeSemanticCreaseMasks(options.semanticMap, W, H)
    : null;
  const eyeMasks = semanticEyeMasks
    ?? EYE_CONFIGS.map((config) => buildSingleEyeMask(detection, config, shapeParams));
  const mask = document.createElement('canvas');
  mask.width = W;
  mask.height = H;
  const maskCtx = mask.getContext('2d');
  if (maskCtx) {
    for (const eyeMask of eyeMasks) maskCtx.drawImage(eyeMask, 0, 0);
  }
  const rgb = hexToRgb(options.hex);
  const [r, g, b] = rgb;

  // Luminance-aware alpha for sparkle shades. See sparkleBaseAlpha header
  // for why a peaked curve (not monotonic boost) is the right shape — mid-
  // bright shades that blend into skin need the help, already-bright shades
  // don't and would paint white over the whole lid if we boosted further.
  let baseAlpha = (options.opacity ?? defaultOpacityFor(options.finish))
    * (options.opacityScale ?? 1);
  const shadeLum = luminance(rgb);
  if (isSparkle) {
    baseAlpha = sparkleBaseAlpha(baseAlpha, shadeLum);
  }

  // Paint a vertical-gradient colour layer (stronger near the lash line,
  // fading toward the brow). The gradient direction follows the average
  // lid→brow vector so it stays aligned with face roll.
  const colour = document.createElement('canvas');
  colour.width = W;
  colour.height = H;
  const cctx = colour.getContext('2d');
  if (!cctx) return { canvas: out };

  for (let i = 0; i < EYE_CONFIGS.length; i++) {
    const grad = lidToBrowGradient(detection, W, H, EYE_CONFIGS[i]!);
    const gradient = cctx.createLinearGradient(grad.sx, grad.sy, grad.ex, grad.ey);
    gradient.addColorStop(0.0, `rgba(${r}, ${g}, ${b}, ${baseAlpha})`);
    gradient.addColorStop(0.18, `rgba(${r}, ${g}, ${b}, ${baseAlpha * 0.66})`);
    gradient.addColorStop(0.38, `rgba(${r}, ${g}, ${b}, ${baseAlpha * 0.28})`);
    gradient.addColorStop(0.62, `rgba(${r}, ${g}, ${b}, ${baseAlpha * 0.08})`);
    gradient.addColorStop(0.82, `rgba(${r}, ${g}, ${b}, 0)`);
    gradient.addColorStop(1.0, `rgba(${r}, ${g}, ${b}, 0)`);

    const eyeColour = document.createElement('canvas');
    eyeColour.width = W;
    eyeColour.height = H;
    const ectx = eyeColour.getContext('2d');
    if (!ectx) continue;
    ectx.fillStyle = gradient;
    ectx.fillRect(0, 0, W, H);
    ectx.globalCompositeOperation = 'destination-in';
    ectx.drawImage(eyeMasks[i]!, 0, 0);
    ectx.globalCompositeOperation = 'source-over';
    cctx.drawImage(eyeColour, 0, 0);

    const creaseMask = semanticCreaseMasks?.[i];
    if (creaseMask) {
      const creaseColour = document.createElement('canvas');
      creaseColour.width = W;
      creaseColour.height = H;
      const crctx = creaseColour.getContext('2d');
      if (!crctx) continue;
      crctx.globalAlpha = isSparkle ? 0.46 : 0.58;
      crctx.fillStyle = gradient;
      crctx.fillRect(0, 0, W, H);
      crctx.globalAlpha = 1;
      crctx.globalCompositeOperation = 'destination-in';
      crctx.drawImage(creaseMask, 0, 0);
      crctx.globalCompositeOperation = 'source-over';
      cctx.drawImage(creaseColour, 0, 0);
    }
  }

  // Soft-light keeps eyelid texture and highlights alive. Source-over made
  // bright shimmers read like a sticker on fair real-world photos; multiply
  // is still reserved for deep mattes where pigment should darken the lid.
  const useMultiply = !isSparkle && shadeLum < 0.42;
  ctx.globalCompositeOperation = useMultiply ? 'multiply' : 'soft-light';
  ctx.drawImage(colour, 0, 0);
  ctx.globalCompositeOperation = 'source-over';

  // A very low source-over deposit makes the shade readable on both eyes
  // when soft-light/multiply gets swallowed by real eyelid lighting. It is
  // clipped by the same feathered masks and carries the vertical fade, so it
  // avoids the hard "sticker" edge from the old full-strength source-over.
  ctx.save();
  ctx.globalAlpha = isSparkle ? 0.22 : 0.28;
  ctx.drawImage(colour, 0, 0);
  ctx.restore();

  // Shimmer/metallic get a second pass that anchors pearl sparkle to the
  // original photo's natural eye-region brightness (brow bone highlight,
  // inner-corner sheen). Without this, shimmer reads as flat tinted skin
  // — we have no physical light model until Session D. The sheen colour
  // is a lightened shade so the sparkle reads as "this shade catching
  // light" rather than "generic white glow".
  if (isSparkle) {
    const sheenTint = lighten(rgb, 0.45);
    const sheen = buildShimmerSheenLayer(source, mask, sheenTint, W, H, 0.55);
    if (sheen) {
      ctx.save();
      ctx.globalAlpha = sheenAlphaFor(finish, shadeLum) * (options.sparkleScale ?? 1);
      ctx.globalCompositeOperation = 'screen';
      ctx.drawImage(sheen, 0, 0);
      ctx.restore();
    }
  }

  // Lash line: thin dark definition above the upper lid. Prefer the
  // semantic beauty mask so QA can inspect it as a first-class target.
  const lashMask = semanticLashLineMask(options.semanticMap, W, H);
  if (lashMask) {
    const lashLayer = document.createElement('canvas');
    lashLayer.width = W;
    lashLayer.height = H;
    const lctx = lashLayer.getContext('2d');
    if (lctx) {
      lctx.fillStyle = 'rgba(20, 14, 10, 0.34)';
      lctx.fillRect(0, 0, W, H);
      lctx.globalCompositeOperation = 'destination-in';
      lctx.drawImage(lashMask, 0, 0);
      ctx.drawImage(lashLayer, 0, 0);
    }
  } else {
    const lw = Math.max(1.05, Math.min(W, H) * 0.0028);
    ctx.save();
    ctx.lineWidth = lw;
    ctx.strokeStyle = 'rgba(20, 14, 10, 0.34)';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const indices of [RIGHT_EYE_UPPER_LID, LEFT_EYE_UPPER_LID]) {
      ctx.beginPath();
      traceLidStroke(ctx, detection, indices);
      ctx.stroke();
    }
    ctx.restore();
  }

  return { canvas: out };
}

function lidToBrowGradient(
  detection: LipDetection,
  W: number,
  H: number,
  config: EyeConfig
): { sx: number; sy: number; ex: number; ey: number } {
  const { landmarks } = detection;

  let lx = 0, ly = 0, bx = 0, by = 0, ln = 0, bn = 0;
  for (const idx of [config.lidMidIdx]) {
    const p = landmarks[idx];
    if (p) { lx += p.x * W; ly += p.y * H; ln++; }
  }
  for (const idx of [config.browMidIdx]) {
    const p = landmarks[idx];
    if (p) { bx += p.x * W; by += p.y * H; bn++; }
  }

  if (!ln || !bn) return { sx: 0, sy: H * 0.55, ex: 0, ey: H * 0.25 };

  const mlx = lx / ln, mly = ly / ln;
  const mbx = bx / bn, mby = by / bn;
  const dx = mbx - mlx, dy = mby - mly;
  const len = Math.hypot(dx, dy);
  if (len < 1) return { sx: mlx, sy: mly, ex: mbx, ey: mby };

  const nx = dx / len, ny = dy / len;
  return {
    sx: mlx - nx * len * 0.10,
    sy: mly - ny * len * 0.10,
    ex: mbx + nx * len * 0.20,
    ey: mby + ny * len * 0.20
  };
}

function traceLidStroke(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  indices: readonly number[]
): void {
  const { landmarks, imageWidth: W, imageHeight: H } = detection;
  const pts: { x: number; y: number }[] = [];
  for (const idx of indices) {
    const p = landmarks[idx];
    if (p) pts.push({ x: p.x * W, y: p.y * H });
  }
  if (pts.length < 2) return;

  // Offset each point slightly upward (toward brow) so the stroke sits
  // just above the natural lash line — reads more like eyeliner application
  // and doesn't get swallowed by the shadow root.
  const offset = Math.max(0.4, Math.min(W, H) * 0.002);
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]!;
    const j = Math.min(i + 1, pts.length - 1);
    const q = pts[j]!;
    const dx = q.x - p.x, dy = q.y - p.y;
    const len = Math.hypot(dx, dy) || 1;
    const px = -dy / len * offset;
    const py = dx / len * offset;
    if (i === 0) ctx.moveTo(p.x + px, p.y + py);
    else ctx.lineTo(p.x + px, p.y + py);
  }
}
